import type { TFunction } from 'i18next';

import type {
  ReceiveLossTableData,
  ReceiveTemperatureRangeDto,
  ReceiveDangerousGoodDto,
  ReceiveSubprojectDto
} from '@/entities/receive';
import { formatDisplayDate } from '@/shared/lib';
import type { ColumnPreset, DataTableColumn } from '@/shared/ui/data-table';

const EMPTY_CELL = '-';

/** `lostType` enum → existing i18n label. */
const LOSS_TYPE_LABELS: Record<string, { key: string; fallback: string }> = {
  DID_NOT_ARRIVE: { key: 'dispatch.never.arrive.label', fallback: 'Will Never arrive' },
  LOST_IN_TRANSIT: { key: 'receive.lostintransit.label', fallback: 'Lost in Transit' }
};

const renderLossType = (code: string, t: TFunction) => {
  const label = LOSS_TYPE_LABELS[code];
  return label ? t(label.key, label.fallback) : code || EMPTY_CELL;
};

/** Scalar quantity as `qty unit` (backend sends per-line scalars, not arrays). */
const renderQuantity = (qty: number | null | undefined, unit: string | undefined) =>
  qty == null ? EMPTY_CELL : `${qty}${unit ? ` ${unit}` : ''}`;

const renderTemperatureRange = (range: ReceiveTemperatureRangeDto | null | undefined) =>
  range ? `${range.minTemp}°C – ${range.maxTemp}°C` : EMPTY_CELL;

const renderDangerousGood = (good: ReceiveDangerousGoodDto | null | undefined) =>
  good ? (
    <span className="whitespace-nowrap" title={good.classDescription}>
      {good.unId}
      {good.description ? ` — ${good.description}` : ''}
    </span>
  ) : (
    EMPTY_CELL
  );

const renderSubprojects = (subprojects: ReceiveSubprojectDto[] | undefined) =>
  subprojects?.length
    ? subprojects.map((subproject) => subproject.subprojectCode || subproject.description || EMPTY_CELL).join(', ')
    : EMPTY_CELL;

/**
 * Lost or Not Sent Items columns. The default-visible set + order match the
 * user's screenshots; the remaining detail columns are hidden by default and
 * surfaced via Customize Columns / the Medical View preset. Column order below
 * is the Customize-Columns dialog order (all 25).
 */
export const getReceiveLossesColumns = (t: TFunction): DataTableColumn<ReceiveLossTableData>[] => [
  {
    id: 'lossDate',
    accessorKey: 'lossDate',
    header: t('dispatch.loss.date.label', 'Date of loss'),
    description: 'Date of loss',
    width: 'min-w-[110px]',
    cell: (row) => <div className="text-xs text-muted-foreground">{formatDisplayDate(row.lossDate)}</div>,
    enableSorting: true,
    sortFieldId: 'lossDate'
  },
  {
    id: 'lostAt',
    accessorKey: 'lostAt',
    header: t('dispatch.loss.location.label', 'Lost at Location'),
    description: 'Location where the loss was reported',
    width: 'min-w-[200px] max-w-[300px] whitespace-normal',
    cell: (row) => row.lostAt || EMPTY_CELL
  },
  {
    id: 'lossType',
    accessorKey: 'lossType',
    header: t('receive.losstype.label', 'Type of loss'),
    description: 'Type of loss',
    width: 'min-w-[140px]',
    cell: (row) => renderLossType(row.lossType, t)
  },
  {
    id: 'transportCompany',
    accessorKey: 'transportCompany',
    header: t('journey.transportCompany.label', 'Transport Company'),
    description: 'Transport company',
    width: 'min-w-[150px]',
    cell: (row) => row.transportCompany || EMPTY_CELL
  },
  {
    id: 'consignmentNumber',
    accessorKey: 'consignmentNumber',
    header: t('request.cmrNumber.label', 'Consignment Number'),
    description: 'Consignment (CMR) number',
    width: 'min-w-[130px]',
    cell: (row) => <span className="font-mono text-sm">{row.consignmentNumber}</span>,
    enableSorting: true,
    sortFieldId: 'consignmentNumber'
  },
  {
    id: 'productDescription',
    accessorKey: 'productDescription',
    header: t('product.description.label', 'Product Category'),
    description: 'Product description',
    width: 'min-w-[160px] max-w-[280px] whitespace-normal',
    cell: (row) => row.productDescription || EMPTY_CELL
  },
  {
    id: 'ownerItemDescription',
    accessorKey: 'ownerItemDescription',
    header: t('common.description', 'Description'),
    description: 'Owner item description',
    width: 'min-w-[180px] max-w-[300px] whitespace-normal',
    cell: (row) => row.ownerItemDescription || EMPTY_CELL
  },
  {
    id: 'lostQty',
    header: t('tracking.servicereq.unitqty.label', 'Inventory Unit Quantity'),
    description: 'Lost quantity in inventory units',
    width: 'min-w-[140px]',
    cell: (row) => renderQuantity(row.lostQty, row.unit)
  },
  {
    id: 'totalWeight',
    accessorKey: 'totalWeight',
    header: t('consignment.columns.weightKg', 'Weight (kg)'),
    description: 'Lost weight in kilograms',
    width: 'min-w-[100px]',
    cell: (row) => (typeof row.totalWeight === 'number' ? row.totalWeight.toFixed(2) : EMPTY_CELL)
  },
  {
    id: 'totalVolume',
    accessorKey: 'totalVolume',
    header: t('consignment.columns.volumeM3', 'Volume (m³)'),
    description: 'Lost volume in cubic meters',
    width: 'min-w-[100px]',
    cell: (row) => (typeof row.totalVolume === 'number' ? row.totalVolume.toFixed(2) : EMPTY_CELL)
  },

  /* --- detail columns (hidden by default) ---------------------------------- */
  {
    id: 'reportingQty',
    header: t('tracking.servicereq.reportingqty.label', 'Reporting Unit Quantity'),
    description: 'Reporting-unit quantity for the lost qty',
    width: 'min-w-[160px]',
    cell: (row) => renderQuantity(row.reportingQty, row.reportingUnit),
    visible: false
  },
  {
    id: 'bundleQty',
    header: t('tracking.servicereq.handlingqty.label', 'Handling Unit Quantity'),
    description: 'Bundle quantity for the lost qty',
    width: 'min-w-[160px]',
    cell: (row) => renderQuantity(row.bundleQty, row.bundleUnit),
    visible: false
  },
  {
    id: 'totalValue',
    accessorKey: 'totalValue',
    header: t('tracking.servicereq.totval.label', 'Total Value (USD)'),
    description: 'Lost value',
    width: 'min-w-[120px]',
    cell: (row) => (typeof row.totalValue === 'number' ? row.totalValue.toLocaleString() : EMPTY_CELL),
    visible: false
  },
  {
    id: 'expiryDate',
    accessorKey: 'expiryDate',
    header: t('requestline.expiryDate.label', 'Expiry Date'),
    description: 'Expiry date of the lost line',
    width: 'min-w-[120px]',
    cell: (row) =>
      row.expiryDate ? (
        <div className="text-xs text-muted-foreground">{formatDisplayDate(row.expiryDate)}</div>
      ) : (
        EMPTY_CELL
      ),
    visible: false
  },
  {
    id: 'temperatureRange',
    header: t('tracking.servicereq.temprange.label', 'Temperature Range'),
    description: 'Cold-storage temperature range',
    width: 'min-w-[140px]',
    cell: (row) => renderTemperatureRange(row.temperatureRange),
    visible: false
  },
  {
    id: 'batchNumber',
    accessorKey: 'batchNumber',
    header: t('requestline.batchNumber.label', 'Batch Number'),
    description: 'Batch number of the lost line',
    width: 'min-w-[130px]',
    cell: (row) => row.batchNumber || EMPTY_CELL,
    visible: false
  },
  {
    id: 'origin',
    accessorKey: 'origin',
    header: t('journey.origin.label', 'Origin Location'),
    description: 'Origin location (parent shipment)',
    width: 'min-w-[180px] max-w-[280px] whitespace-normal',
    cell: (row) => row.origin || EMPTY_CELL,
    visible: false
  },
  {
    id: 'destination',
    accessorKey: 'destination',
    header: t('convoy.destination.label', 'Destination'),
    description: 'Destination location (parent shipment)',
    width: 'min-w-[180px] max-w-[280px] whitespace-normal',
    cell: (row) => row.destination || EMPTY_CELL,
    visible: false
  },
  {
    id: 'waybill',
    accessorKey: 'waybill',
    header: t('cmrlist.waybill.label', 'Waybill'),
    description: 'Shipment document (waybill) number',
    width: 'min-w-[130px]',
    cell: (row) => row.waybill || EMPTY_CELL,
    visible: false
  },
  {
    id: 'plate',
    accessorKey: 'plate',
    header: t('journey.vehicleIdnf.label', 'Vehicle Id'),
    description: 'Vehicle plate/identifier',
    width: 'min-w-[120px]',
    cell: (row) => row.plate || EMPTY_CELL,
    visible: false
  },
  {
    id: 'senderOrganization',
    accessorKey: 'senderOrganization',
    header: t('newcmr.senderorg.label', 'Sender Organization'),
    description: 'Sender organization',
    width: 'min-w-[150px] max-w-[250px] whitespace-normal',
    cell: (row) => row.senderOrganization || EMPTY_CELL,
    visible: false
  },
  {
    id: 'receiverOrganization',
    accessorKey: 'receiverOrganization',
    header: t('newcmr.receiverorg.label', 'Receiver Organization'),
    description: 'Receiver organization',
    width: 'min-w-[150px] max-w-[250px] whitespace-normal',
    cell: (row) => row.receiverOrganization || EMPTY_CELL,
    visible: false
  },
  {
    id: 'dangerousGood',
    header: t('requestline.dangerousGoodsUnid.label', 'Dangerous Goods UN Identification number'),
    description: 'Dangerous good of the lost line',
    width: 'min-w-[180px]',
    cell: (row) => renderDangerousGood(row.dangerousGood),
    visible: false
  },
  {
    id: 'ownersReference',
    accessorKey: 'ownersReference',
    header: t('request.ownersRef.label', "Owner's Reference"),
    description: "Owner's reference",
    width: 'min-w-[130px]',
    cell: (row) => row.ownersReference || EMPTY_CELL,
    visible: false
  },
  {
    id: 'subprojects',
    header: t('subproject.idnf.label', 'Sub-project code'),
    description: 'Distinct subprojects of the parent shipment',
    width: 'min-w-[130px]',
    cell: (row) => renderSubprojects(row.subprojects),
    visible: false
  }
];

/** Column presets — Medical View mirrors the screenshot's Medical View marks. */
export const getReceiveLossesColumnPresets = (t: TFunction): ColumnPreset[] => [
  {
    id: 'medical-view',
    label: t('gen.customize.medview.button', 'Medical View'),
    columnIds: [
      'lossDate',
      'lostAt',
      'lossType',
      'consignmentNumber',
      'ownerItemDescription',
      'lostQty',
      'totalValue',
      'expiryDate',
      'temperatureRange',
      'batchNumber',
      'receiverOrganization'
    ]
  }
];
